/**
 * Memory Guard — Proactive OOM watchdog
 *
 * Por quê:
 * Em Samsung Tizen consumer (e outras TVs) o WebView vaza memória
 * renderer/nativa durante playback contínuo. Após ~2-4h a OS OOM-mata o
 * processo — e como TV consumer NÃO tem auto-relaunch, a TV cai pra outro
 * canal e o player some da tela até alguém reabrir manualmente.
 *
 * O leak é pressão de memória renderer/nativa (não um leak de objeto JS
 * rastreável), então a mitigação confiável é um RESET periódico. Este módulo
 * observa a heap (via `performance.memory`, quando disponível) + uptime e
 * reseta o app PROATIVAMENTE (antes do kill da OS) via
 * `platform.restart('refresh')` — a mesma abstração usada pelo
 * PLAYER_RESTART_PLAN (Tizen/webOS → location.reload, Android → Capacitor
 * App.restart, Browser → reload). O reset re-inicia o boot; como o kiosk
 * já re-trava no boot, não há nada a fazer aqui pra kiosk — só deixar o boot
 * seguir seu curso normal.
 *
 * Anti-loop (crítico): se um reset NÃO recupera memória (heap continua alta
 * após o reload), o boot-grace + min-interval — ambos persistidos em
 * localStorage — impedem um loop apertado de reloads (no máximo ~1 reset por
 * `minIntervalMin`). O timestamp do último reset é lido no boot ANTES do
 * primeiro reset possível.
 *
 * Observabilidade: o evento de analytics `memory_guard_reload` é emitido (e
 * persistido no IndexedDB) ANTES do restart, então sobrevive ao reload e
 * sincroniza no boot seguinte (mesmo padrão do `player_crash`). NÃO
 * aguardamos flush de rede — o reload mataria a request; confiamos na
 * persistência do IndexedDB do analytics.
 */
import type { IPlatformService } from '~/services/platform/types';
import type { TrackEventInput } from '~/services/analytics/types';

/** Chave persistida do timestamp (ms) do último reset — sobrevive ao reload. */
const LAST_RESET_KEY = 'efflux_mem_guard_last_reset_v1';

/** Cadência de checagem (fixa) — separada do `minIntervalMin` (anti-loop). */
const CHECK_INTERVAL_MS = 30_000;

/** Atraso antes do restart: dá tempo do `addEvent` (IndexedDB, assíncrono) do
 *  analytics COMMITAR — senão o reload aborta a transação e o evento de
 *  observabilidade se perde. Não é time-critical (o reset pode esperar 1s). */
const RESTART_DELAY_MS = 1_000;

/** Superfície mínima do platform service que o guard consome. */
export type MemoryGuardPlatform = Pick<
  IPlatformService,
  'restart' | 'platform'
>;

/** Superfície mínima do analytics service que o guard consome. */
export interface MemoryGuardAnalytics {
  trackEvent(input: TrackEventInput): void | Promise<void>;
}

// =============================================================================
// PURE DECISION FUNCTION (unit-testable)
// =============================================================================

export interface ShouldResetForMemoryInput {
  /** Heap usada em MB, ou `null` quando `performance.memory` não existe. */
  heapUsedMB: number | null;
  /** Minutos desde o boot/instalação do guard nesta sessão. */
  uptimeMin: number;
  /** Minutos desde o último reset (persistido). `Infinity` se nunca resetou. */
  minSinceLastReset: number;
  /** Janela de graça pós-boot — não reseta antes disso. */
  bootGraceMin: number;
  /** Intervalo mínimo entre resets (anti-loop). */
  minIntervalMin: number;
  /** Limiar de heap (MB) que dispara reset. */
  heapThresholdMB: number;
  /** Teto de uptime (min) que dispara reset (rede de segurança). */
  maxUptimeMin: number;
  /** Se o guard está habilitado. */
  enabled: boolean;
}

export interface ShouldResetForMemoryResult {
  reset: boolean;
  reason: 'heap' | 'uptime' | null;
}

/**
 * Decisão pura (sem I/O) de resetar ou não. Ordem:
 *   1. desabilitado → não
 *   2. dentro do boot-grace → não
 *   3. dentro do min-interval (anti-loop) → não
 *   4. heap ≥ limiar → reset (reason 'heap')  ← checado ANTES do uptime
 *   5. uptime ≥ teto → reset (reason 'uptime')
 *   6. senão → não
 *
 * Se `heapUsedMB` for `null` (sem `performance.memory`), a checagem de heap é
 * pulada e só o uptime pode disparar.
 */
export function shouldResetForMemory(
  input: ShouldResetForMemoryInput,
): ShouldResetForMemoryResult {
  const {
    heapUsedMB,
    uptimeMin,
    minSinceLastReset,
    bootGraceMin,
    minIntervalMin,
    heapThresholdMB,
    maxUptimeMin,
    enabled,
  } = input;

  const NO: ShouldResetForMemoryResult = { reset: false, reason: null };

  if (!enabled) return NO;
  // Boot grace — nunca reseta logo após o boot. Também dá tempo pro app
  // recém-recarregado assentar antes de re-medir (par com o last-reset ts).
  if (uptimeMin < bootGraceMin) return NO;
  // ANTI-LOOP — se o reset anterior NÃO recuperou memória (heap continua
  // alta), isso impede um loop apertado de reloads (máx ~1 reset por
  // minIntervalMin). Lido do last-reset ts persistido → sobrevive ao reload.
  if (minSinceLastReset < minIntervalMin) return NO;

  // Heap primeiro (o sinal real); uptime é rede de segurança.
  if (heapUsedMB != null && heapUsedMB >= heapThresholdMB) {
    return { reset: true, reason: 'heap' };
  }
  if (uptimeMin >= maxUptimeMin) {
    return { reset: true, reason: 'uptime' };
  }
  return NO;
}

// =============================================================================
// CONFIG (env com defaults conservadores)
// =============================================================================

export interface MemoryGuardConfig {
  enabled: boolean;
  heapThresholdMB: number;
  maxUptimeMin: number;
  minIntervalMin: number;
  bootGraceMin: number;
}

/** `Number()` com guarda: fallback se não-finito ou ≤ 0. */
function envNum(value: string | undefined, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * Resolve a config a partir de `import.meta.env`. Defaults conservadores:
 * **DESLIGADO por padrão (opt-in via VITE_MEMORY_GUARD === 'true')** —
 * Renato 2026-07-20: o reload proativo VIOLA o contrato de impressão (reinit
 * só a cada 24h + reload reseta a ordem do conteúdo). Feature PRATELEIRA: o
 * código fica pra retomar a investigação do vazamento (pausada), mas NÃO roda
 * em prod. heap 150MB, uptime 3h, min-interval 30min, boot-grace 10min.
 */
export function resolveConfig(): MemoryGuardConfig {
  const env = import.meta.env;
  // Default 3h: a forense mostrou ciclo de OOM ~2-3h em playback contínuo, e a
  // métrica de heap na Tizen é instável (pode falhar como gatilho primário) —
  // então o backstop de uptime tem que ser MENOR que o OOM real. Calibrar fino
  // na bancada. Override via VITE_MEMORY_GUARD_MAX_UPTIME_H.
  const maxUptimeH = envNum(env.VITE_MEMORY_GUARD_MAX_UPTIME_H, 3);
  return {
    enabled: env.VITE_MEMORY_GUARD === 'true',
    heapThresholdMB: envNum(env.VITE_MEMORY_GUARD_HEAP_MB, 150),
    maxUptimeMin: maxUptimeH * 60,
    minIntervalMin: envNum(env.VITE_MEMORY_GUARD_MIN_INTERVAL_MIN, 30),
    bootGraceMin: envNum(env.VITE_MEMORY_GUARD_BOOT_GRACE_MIN, 10),
  };
}

// =============================================================================
// I/O HELPERS
// =============================================================================

/** Lê heap usada em MB, ou `null` quando `performance.memory` não existe. */
function readHeapUsedMB(): number | null {
  try {
    // performance.memory existe em V8 (Chrome/Tizen/Capacitor Android) mas
    // não no spec — cast condicional pra não quebrar o TS.
    const perfMem = (
      performance as unknown as { memory?: { usedJSHeapSize: number } }
    ).memory;
    if (!perfMem || typeof perfMem.usedJSHeapSize !== 'number') return null;
    return perfMem.usedJSHeapSize / 1048576;
  } catch {
    return null;
  }
}

/** Minutos desde o último reset persistido. `Infinity` se nunca resetou. */
function readMinSinceLastReset(now: number): number {
  try {
    const raw = localStorage.getItem(LAST_RESET_KEY);
    if (!raw) return Number.POSITIVE_INFINITY;
    const ts = Number(raw);
    if (!Number.isFinite(ts) || ts <= 0) return Number.POSITIVE_INFINITY;
    return (now - ts) / 60_000;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

/** Persiste o timestamp do reset (sobrevive ao reload → anti-loop + grace). */
function persistLastReset(now: number): void {
  try {
    localStorage.setItem(LAST_RESET_KEY, String(now));
  } catch {
    /* localStorage cheio/indisponível — ignora */
  }
}

// =============================================================================
// INSTALL / UNINSTALL
// =============================================================================

let installed = false;
let timer: ReturnType<typeof setInterval> | null = null;
let installTs = 0;

/**
 * Instala o watchdog. Idempotente — chamar 1× no boot. No-op fora do browser
 * (SSR/testes sem window) e quando desabilitado via env.
 */
export function installMemoryGuard(
  platform: MemoryGuardPlatform,
  analytics: MemoryGuardAnalytics,
): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  installTs = Date.now();

  const config = resolveConfig();
  if (!config.enabled) {
    console.log('[MemoryGuard] disabled via VITE_MEMORY_GUARD=false');
    return;
  }

  const tick = (): void => {
    const now = Date.now();
    const heapUsedMB = readHeapUsedMB();
    const uptimeMin = (now - installTs) / 60_000;
    const minSinceLastReset = readMinSinceLastReset(now);

    const decision = shouldResetForMemory({
      heapUsedMB,
      uptimeMin,
      minSinceLastReset,
      bootGraceMin: config.bootGraceMin,
      minIntervalMin: config.minIntervalMin,
      heapThresholdMB: config.heapThresholdMB,
      maxUptimeMin: config.maxUptimeMin,
      enabled: config.enabled,
    });

    if (!decision.reset) return;

    const reason = decision.reason;
    const heapRounded = heapUsedMB != null ? Math.round(heapUsedMB) : null;
    const uptimeRounded = Math.round(uptimeMin);

    // (a) Emitir analytics ANTES do restart — persiste no IndexedDB e
    // sincroniza no boot seguinte (não aguardamos flush de rede: o reload
    // mataria a request).
    try {
      void analytics.trackEvent({
        type: 'memory_guard_reload',
        data: {
          heapUsedMB: heapRounded,
          reason: reason ?? undefined,
          uptimeMin: uptimeRounded,
          platform: platform.platform,
        },
      });
    } catch (err) {
      console.error('[MemoryGuard] failed to emit analytics', err);
    }

    // (b) Persistir o last-reset ts (anti-loop + boot-grace sobrevivem ao reload).
    persistLastReset(now);

    // (c) Marker line observável no console.
    console.warn(
      `[MemoryGuard] ⚠️ PROACTIVE RESET reason=${reason} heap=${
        heapRounded ?? '?'
      }MB uptime=${uptimeRounded}min — reloading BEFORE OS OOM-kill`,
    );

    // (d) Reset via abstração cross-platform, com pequeno atraso pra o addEvent
    // (IndexedDB, assíncrono) do analytics COMMITAR antes do reload — senão a
    // transação aborta e o evento de observabilidade se perde. Re-inicia o boot
    // (o kiosk re-trava no boot — nada a fazer aqui). `persistLastReset` já
    // rodou, então o próximo tick (30s) é barrado pelo anti-loop — sem reset
    // duplo durante o atraso.
    setTimeout(() => {
      void platform.restart('refresh').catch((err) => {
        console.error('[MemoryGuard] restart failed', err);
      });
    }, RESTART_DELAY_MS);
  };

  timer = setInterval(tick, CHECK_INTERVAL_MS);
  console.log(
    `[MemoryGuard] ✅ installed (heap≥${config.heapThresholdMB}MB | uptime≥${config.maxUptimeMin}min | grace ${config.bootGraceMin}min | min-interval ${config.minIntervalMin}min | check ${CHECK_INTERVAL_MS / 1000}s)`,
  );
}

/** Para o watchdog e reseta o estado. Usado em testes e no cleanup do effect. */
export function uninstallMemoryGuard(): void {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  installed = false;
  installTs = 0;
}
